import { useState } from 'react';
import { TestimonialsConfig, TestimonialItemConfig } from '@/lib/themeConfig';
import { SaveButton } from './GeneralSection';
import ImageUploader from '../ImageUploader';

interface Props { data: TestimonialsConfig; onChange: (data: TestimonialsConfig) => void; }

const emptyItem = (): TestimonialItemConfig => ({
  id: Date.now().toString(),
  name: '',
  location: '',
  avatar_url: '',
  rating: 5,
  text: '',
  product_name: '',
});

export default function TestimonialsSectionEditor({ data, onChange }: Props) {
  const [local, setLocal] = useState<TestimonialsConfig>(data);
  const [activeIdx, setActiveIdx] = useState(0);
  const [tab, setTab] = useState<'header' | 'items' | 'stats'>('items');
  const [saved, setSaved] = useState(false);

  const handleSave = () => { onChange(local); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  // Header updates
  const updateHeader = (key: 'badge' | 'title', val: string) =>
    setLocal(p => ({ ...p, [key]: val }));

  // Stats updates
  const updateStat = (i: number, key: 'value' | 'label', val: string) =>
    setLocal(p => ({ ...p, stats: p.stats.map((s, idx) => idx === i ? { ...s, [key]: val } : s) }));

  // Item updates
  const updateItem = (i: number, key: keyof TestimonialItemConfig, val: string | number) =>
    setLocal(p => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [key]: val } : it) }));

  const addItem = () => {
    const next = [...local.items, emptyItem()];
    setLocal(p => ({ ...p, items: next }));
    setActiveIdx(next.length - 1);
  };

  const removeItem = (i: number) => {
    if (local.items.length <= 1) return;
    const next = local.items.filter((_, idx) => idx !== i);
    setLocal(p => ({ ...p, items: next }));
    setActiveIdx(Math.min(activeIdx, next.length - 1));
  };

  const item = local.items[activeIdx];

  return (
    <div className="max-w-3xl space-y-6">
      {/* Tab Switcher */}
      <div className="bg-white rounded-2xl p-4 flex gap-2">
        {[
          { id: 'items', label: 'Yorumlar', icon: 'ri-chat-quote-line' },
          { id: 'stats', label: 'İstatistikler', icon: 'ri-bar-chart-line' },
          { id: 'header', label: 'Başlık', icon: 'ri-text' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as typeof tab)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              tab === t.id ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <i className={t.icon} /> {t.label}
          </button>
        ))}
      </div>

      {/* Header Tab */}
      {tab === 'header' && (
        <div className="bg-white rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-stone-700 border-b border-stone-100 pb-3">Bölüm Başlığı</h2>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Rozet</label>
            <input
              type="text"
              value={local.badge}
              onChange={e => updateHeader('badge', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Başlık <span className="text-stone-400">(yeni satır için \n)</span></label>
            <textarea
              value={local.title}
              onChange={e => updateHeader('title', e.target.value)}
              rows={2}
              className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all resize-none"
            />
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && (
        <div className="bg-white rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-stone-700 border-b border-stone-100 pb-3">İstatistik Kartları</h2>
          <div className="grid grid-cols-2 gap-4">
            {local.stats.map((stat, i) => (
              <div key={i} className="p-4 bg-stone-50 rounded-xl space-y-2">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Değer</label>
                  <input
                    type="text"
                    value={stat.value}
                    onChange={e => updateStat(i, 'value', e.target.value)}
                    placeholder="12.400+"
                    className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Etiket</label>
                  <input
                    type="text"
                    value={stat.label}
                    onChange={e => updateStat(i, 'label', e.target.value)}
                    placeholder="Mutlu Müşteri"
                    className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items Tab */}
      {tab === 'items' && (
        <>
          {/* Yorum Tabs */}
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 flex-wrap">
              {local.items.map((it, i) => (
                <button
                  key={it.id}
                  onClick={() => setActiveIdx(i)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeIdx === i ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {it.name || `Yorum ${i + 1}`}
                </button>
              ))}
              <button
                onClick={addItem}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-add-line" /> Yeni Yorum
              </button>
            </div>
          </div>

          {/* Yorum Editörü */}
          {item && (
            <div className="bg-white rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-stone-700">Yorum {activeIdx + 1}</h2>
                {local.items.length > 1 && (
                  <button
                    onClick={() => removeItem(activeIdx)}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line" /> Sil
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Ad Soyad</label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={e => updateItem(activeIdx, 'name', e.target.value)}
                    placeholder="Ayşe Kılıç"
                    className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Şehir</label>
                  <input
                    type="text"
                    value={item.location}
                    onChange={e => updateItem(activeIdx, 'location', e.target.value)}
                    placeholder="İstanbul"
                    className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Puan (1-5)</label>
                  <div className="flex gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => updateItem(activeIdx, 'rating', star)}
                        className={`w-8 h-8 flex items-center justify-center cursor-pointer transition-colors ${
                          star <= item.rating ? 'text-amber-500' : 'text-stone-300'
                        }`}
                      >
                        <i className="ri-star-fill" />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Ürün Adı (opsiyonel)</label>
                  <input
                    type="text"
                    value={item.product_name}
                    onChange={e => updateItem(activeIdx, 'product_name', e.target.value)}
                    placeholder="Etiyopya Yirgacheffe"
                    className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Yorum Metni</label>
                <textarea
                  value={item.text}
                  onChange={e => updateItem(activeIdx, 'text', e.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="Müşteri yorumu..."
                  className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all resize-none"
                />
                <p className="text-xs text-stone-400 mt-1 text-right">{item.text.length}/500</p>
              </div>

              <ImageUploader
                label="Profil Fotoğrafı"
                value={item.avatar_url}
                onChange={val => updateItem(activeIdx, 'avatar_url', val)}
                previewHeight="h-24"
                hint="Kare format önerilir (200×200px)"
              />
            </div>
          )}
        </>
      )}

      <SaveButton saved={saved} onSave={handleSave} />
    </div>
  );
}
